import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import {
  useEffect,
} from "react";

import {
  Controller,
  useForm,
} from "react-hook-form";

const fieldSx = {
  "& .MuiInputBase-root": {
    borderRadius: 0,
    fontSize: "0.72rem",
  },

  "& .MuiInputLabel-root": {
    fontSize: "0.72rem",
  },

  "& .MuiFormHelperText-root": {
    fontSize: "0.62rem",
  },
};

const EMPTY_VALUES = {
  device_serial_id: "",
  device_name: "",
  device_model: "",
  last_public_ip: "",
  firmware_version: "",
  location_name: "",
  notes: "",
  is_active: true,
  shop_ids: [],
};

function buildDefaultValues(
  firewall,
  mode,
) {
  if (
    mode !== "edit" ||
    !firewall
  ) {
    return EMPTY_VALUES;
  }

  return {
    device_serial_id:
      firewall.device_serial_id ||
      "",

    device_name:
      firewall.device_name ||
      "",

    device_model:
      firewall.device_model ||
      "",

    last_public_ip:
      firewall.last_public_ip ||
      "",

    firmware_version:
      firewall.firmware_version ||
      "",

    location_name:
      firewall.location_name ||
      "",

    notes:
      firewall.notes ||
      "",

    is_active:
      Boolean(
        firewall.is_active,
      ),

    shop_ids:
      Array.isArray(
        firewall.shops,
      )
        ? firewall.shops.map(
            (shop) => shop.id,
          )
        : [],
  };
}

export default function FirewallFormDialog({
  open,
  mode = "create",
  firewall,
  shops = [],
  loading = false,
  onClose,
  onSubmit,
}) {
  const {
    control,
    handleSubmit,
    reset,
    formState: {
      errors,
    },
  } = useForm({
    defaultValues:
      EMPTY_VALUES,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(
      buildDefaultValues(
        firewall,
        mode,
      ),
    );
  }, [
    open,
    firewall,
    mode,
    reset,
  ]);

  const submit = (
    values,
  ) => {
    const payload = {
      device_serial_id:
        values.device_serial_id
          .trim()
          .toUpperCase(),

      device_name:
        values.device_name.trim() ||
        null,

      device_model:
        values.device_model.trim() ||
        null,

      last_public_ip:
        values.last_public_ip.trim() ||
        null,

      firmware_version:
        values.firmware_version.trim() ||
        null,

      location_name:
        values.location_name.trim() ||
        null,

      notes:
        values.notes.trim() ||
        null,

      /*
       * Actual boolean for Laravel.
       */
      is_active:
        Boolean(
          values.is_active,
        ),
    };

    /*
     * The backend update method does not
     * accept shop_ids. Shops are managed
     * through assignment endpoints.
     */
    if (mode === "create") {
      payload.shop_ids =
        Array.isArray(
          values.shop_ids,
        )
          ? values.shop_ids
          : [];
    }

    onSubmit(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={
        loading
          ? undefined
          : onClose
      }
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          width:
            "min(1080px, calc(100vw - 32px))",
          maxWidth: "1080px",
          m: {
            xs: 1,
            md: 2,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 1.5,
          px: 2,
          fontSize: "0.9rem",
          fontWeight: 850,
          borderBottom:
            "1px solid #E3E7EC",
        }}
      >
        {mode === "edit"
          ? "Edit Sophos firewall"
          : "Register Sophos firewall"}
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          bgcolor: "#FBFCFD",
        }}
      >
        <Box
          component="form"
          id="firewall-form"
          onSubmit={handleSubmit(
            submit,
          )}
          sx={{
            p: {
              xs: 1.5,
              md: 2,
            },
          }}
        >
          <Stack spacing={2}>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 850,
                }}
              >
                Appliance details
              </Typography>

              <Typography
                sx={{
                  color: "#667085",
                  fontSize: "0.64rem",
                }}
              >
                Enter the Sophos device
                identity and firmware
                information.
              </Typography>
            </Box>

            <Grid
              container
              spacing={1.4}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <Controller
                  name="device_serial_id"
                  control={control}
                  rules={{
                    required:
                      "Device serial ID is required.",
                  }}
                  render={({
                    field,
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label="Device serial ID"
                      placeholder="XGS2100-ABC123456"
                      error={Boolean(
                        errors.device_serial_id,
                      )}
                      helperText={
                        errors
                          .device_serial_id
                          ?.message
                      }
                      sx={fieldSx}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Controller
                  name="device_name"
                  control={control}
                  render={({
                    field,
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Device name"
                      placeholder="Head Office Firewall"
                      sx={fieldSx}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Controller
                  name="device_model"
                  control={control}
                  render={({
                    field,
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Device model"
                      placeholder="Sophos XGS 2100"
                      sx={fieldSx}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Controller
                  name="firmware_version"
                  control={control}
                  render={({
                    field,
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Firmware version"
                      placeholder="SFOS 20.0.1"
                      sx={fieldSx}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Controller
                  name="last_public_ip"
                  control={control}
                  rules={{
                    pattern: {
                      value:
                        /^((25[0-5]|2[0-4]\d|1?\d?\d)(\.|$)){4}$/,
                      message:
                        "Enter a valid IPv4 address.",
                    },
                  }}
                  render={({
                    field,
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Last public IP"
                      placeholder="41.60.120.10"
                      error={Boolean(
                        errors.last_public_ip,
                      )}
                      helperText={
                        errors
                          .last_public_ip
                          ?.message
                      }
                      sx={fieldSx}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Controller
                  name="location_name"
                  control={control}
                  render={({
                    field,
                  }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Location name"
                      placeholder="Harare Head Office"
                      sx={fieldSx}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {mode ===
              "create" && (
              <>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 850,
                    }}
                  >
                    Initial shop assignments
                  </Typography>

                  <Typography
                    sx={{
                      color: "#667085",
                      fontSize: "0.64rem",
                    }}
                  >
                    Select shops that should
                    initially use this
                    firewall.
                  </Typography>
                </Box>

                <Controller
                  name="shop_ids"
                  control={control}
                  render={({
                    field,
                  }) => (
                    <TextField
                      select
                      fullWidth
                      label="Assigned shops"
                      value={
                        field.value ||
                        []
                      }
                      onChange={(
                        event,
                      ) =>
                        field.onChange(
                          event.target
                            .value,
                        )
                      }
                      SelectProps={{
                        multiple: true,

                        renderValue: (
                          selected,
                        ) => {
                          if (
                            !selected
                              ?.length
                          ) {
                            return "No shops selected";
                          }

                          return selected
                            .map(
                              (id) => {
                                const shop =
                                  shops.find(
                                    (
                                      item,
                                    ) =>
                                      item.id ===
                                      id,
                                  );

                                return (
                                  shop?.shop_name ||
                                  shop?.shop_code ||
                                  id
                                );
                              },
                            )
                            .join(", ");
                        },
                      }}
                      sx={fieldSx}
                    >
                      {shops.map(
                        (shop) => {
                          const checked =
                            (
                              field.value ||
                              []
                            ).includes(
                              shop.id,
                            );

                          return (
                            <MenuItem
                              key={
                                shop.id
                              }
                              value={
                                shop.id
                              }
                              sx={{
                                fontSize:
                                  "0.72rem",
                              }}
                            >
                              <Checkbox
                                checked={
                                  checked
                                }
                                size="small"
                              />

                              <Box>
                                <Typography
                                  sx={{
                                    fontSize:
                                      "0.72rem",
                                    fontWeight: 750,
                                  }}
                                >
                                  {shop.shop_name ||
                                    shop.shop_code}
                                </Typography>

                                <Typography
                                  sx={{
                                    color:
                                      "#667085",
                                    fontSize:
                                      "0.62rem",
                                  }}
                                >
                                  {shop.shop_code ||
                                    shop.location ||
                                    ""}
                                </Typography>
                              </Box>
                            </MenuItem>
                          );
                        },
                      )}
                    </TextField>
                  )}
                />
              </>
            )}

            <Box>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 850,
                }}
              >
                Additional information
              </Typography>

              <Typography
                sx={{
                  color: "#667085",
                  fontSize: "0.64rem",
                }}
              >
                Record operational notes
                and device availability.
              </Typography>
            </Box>

            <Controller
              name="notes"
              control={control}
              render={({
                field,
              }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={4}
                  label="Notes"
                  placeholder="Add device, network or deployment notes"
                  sx={fieldSx}
                />
              )}
            />

            <Controller
              name="is_active"
              control={control}
              render={({
                field,
              }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(
                        field.value,
                      )}
                      onChange={(
                        _,
                        checked,
                      ) =>
                        field.onChange(
                          checked,
                        )
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        sx={{
                          fontSize:
                            "0.72rem",
                          fontWeight: 750,
                        }}
                      >
                        Active firewall
                      </Typography>

                      <Typography
                        sx={{
                          color:
                            "#667085",
                          fontSize:
                            "0.62rem",
                        }}
                      >
                        Allow this firewall
                        to be used for active
                        shop assignments.
                      </Typography>
                    </Box>
                  }
                />
              )}
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2,
          py: 1.3,
          borderTop:
            "1px solid #E3E7EC",
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          color="inherit"
          sx={{
            borderRadius: 0,
            fontSize: "0.72rem",
          }}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          form="firewall-form"
          variant="contained"
          disabled={loading}
          sx={{
            borderRadius: 0,
            minWidth: 125,
            fontSize: "0.72rem",
          }}
        >
          {loading
            ? "Saving..."
            : mode === "edit"
              ? "Save changes"
              : "Register firewall"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}